import { useState } from "react";

/**
 * Edits a short list of free-text values (pick-list options, shortcut amounts).
 * Values are added and removed rather than typed as one delimited string, so a
 * stray comma can't silently merge two entries.
 */
export function ListEditor({
  values,
  onChange,
  placeholder,
  numeric = false,
  minimum = 0,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  numeric?: boolean;
  /** Refuse to go below this many entries, for lists that can't be empty. */
  minimum?: number;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  function add() {
    const value = draft.trim();
    if (!value) return;
    if (numeric && !Number.isFinite(Number(value))) {
      setError("Enter a number");
      return;
    }
    if (values.some((v) => v.toLowerCase() === value.toLowerCase())) {
      setError("That one is already in the list");
      return;
    }
    onChange([...values, value]);
    setDraft("");
    setError(null);
  }

  function remove(index: number) {
    if (values.length <= minimum) {
      setError(`Keep at least ${minimum} ${minimum === 1 ? "entry" : "entries"}`);
      return;
    }
    onChange(values.filter((_, i) => i !== index));
    setError(null);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {values.map((value, index) => (
          <span key={`${value}-${index}`} className="chip bg-ink-100 text-ink-700 ring-1 ring-ink-200">
            <span className={numeric ? "tnum" : undefined}>{value}</span>
            <button
              type="button"
              onClick={() => remove(index)}
              className="ml-1 text-ink-400 hover:text-red-700"
              aria-label={`Remove ${value}`}
            >
              ✕
            </button>
          </span>
        ))}
        {values.length === 0 && <span className="text-xs text-ink-400">Nothing in this list yet.</span>}
      </div>

      <div className="mt-2 flex gap-2">
        <input
          className="input flex-1"
          value={draft}
          placeholder={placeholder}
          inputMode={numeric ? "numeric" : undefined}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
          }}
        />
        <button type="button" className="btn-secondary" onClick={add}>Add</button>
      </div>
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  );
}
